import { html, nothing, svg, type TemplateResult } from "lit";
import type { OrderTotals } from "../../../checkout/order-totals";
import { renderCheckoutTotals } from "../checkout-totals.view";
import { formatMajorAmount, formatShortAmount } from "../../format-money";
import { findTier, type LineItem } from "../../../checkout/checkout-math";
import type { FormQuestion } from "../../../data/live";
import { isPack, type PricingTier } from "../../data/mock-pricing";
import type { Attendee } from "./booking-checkout.model";

const ICON_LOCK = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z"/><polyline points="9 12 11 14 15 10"/></svg>`;

export interface BookingSummaryViewModel {
  items: readonly LineItem[];
  deferred?: boolean | undefined;
  attendeeCount: number;
  image: string;
  thumbInitials: string;
  displayTitle: string;
  displayMetaParts: readonly string[];
  duration: string;
  isAppointment: boolean;
  appointmentLocation: string;
  appointmentPrice: number;
  creditOnly: boolean;
  subtotal: number;
  total: number;
  promoCode: string;
  promoBusy: boolean;
  checkoutLocked?: boolean;
  promoError: string;
  discount: number;
  /** The server's quote for this cart (tax + pass-on fees), once fetched. */
  quote?: OrderTotals | undefined;
  /** Org ISO-4217 currency; USD when unset. */
  currency?: string | undefined;
  /**
   * PLAT-6800: an attendee has no payment option yet, so there is no price to
   * show. The summary asks for a choice instead of quoting a fallback tier.
   */
  payChoicePending?: boolean;
  onChoosePayment?: () => void;
  onPromoCodeChange: (value: string) => void;
  onApplyPromo: () => void;
}

export function renderBookingSummary(
  model: BookingSummaryViewModel,
): TemplateResult {
  return html`
    <aside class="card summary">
      <h3>Order Summary</h3>
      <div class="sum-item">
        <div class="thumb">
          ${model.image
            ? html`<img src=${model.image} alt="" />`
            : html`<span class="thumb-initials">${model.thumbInitials}</span>`}
          ${model.attendeeCount > 1
            ? html`<span class="badge-count">${model.attendeeCount}</span>`
            : nothing}
        </div>
        <div style="min-width:0">
          <div class="nm">${model.displayTitle}</div>
          <div class="mt">
            ${model.displayMetaParts.filter(Boolean).join(" · ")}
          </div>
          <div class="mt">
            ${[
              model.duration,
              model.isAppointment ? model.appointmentLocation : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        ${model.creditOnly || model.payChoicePending
          ? nothing
          : html`<span class="pr"
              >${formatShortAmount(model.subtotal, model.currency)}</span
            >`}
      </div>

      ${model.payChoicePending
        ? html`<div class="pay-pending">
            <p>Choose a payment option to see your total.</p>
            <button
              type="button"
              class="link"
              @click=${() => model.onChoosePayment?.()}
            >
              See payment options
            </button>
          </div>`
        : renderSummaryPricing(model)}
    </aside>
  `;
}

/** Line items, promo code and totals — shown once every attendee has a price. */
function renderSummaryPricing(model: BookingSummaryViewModel): TemplateResult {
  return html`
      <div class="breakdown">
        ${model.items.map(
          (lineItem) =>
            html`<div class="li">
              <span
                >${lineItem.label}
                <span class="sub">· ${lineItem.sub}</span></span
              >
              <span class=${lineItem.free ? "free" : ""}
                >${model.deferred ? "Membership booking" : lineItem.credit
                  ? model.isAppointment && model.appointmentPrice <= 0
                    ? "1 credit"
                    : "Credit used"
                  : lineItem.free
                    ? "Free"
                    : formatMajorAmount(lineItem.amount, model.currency)}</span
              >
            </div>`,
        )}
      </div>

      ${model.creditOnly || model.deferred
        ? nothing
        : html`<div class="promo">
              <div class="lbl">Discount code</div>
              <div class="row">
                <input
                  placeholder="Code"
                  ?disabled=${model.checkoutLocked}
                  .value=${model.promoCode}
                  @input=${(event: Event) =>
                    model.onPromoCodeChange(
                      (event.target as HTMLInputElement).value,
                    )}
                />
                <button
                  ?disabled=${model.promoBusy || model.checkoutLocked}
                  @click=${model.onApplyPromo}
                >
                  ${model.promoBusy ? "…" : "Apply"}
                </button>
              </div>
              ${model.promoError
                ? html`<div class="promo-msg err">${model.promoError}</div>`
                : model.discount
                  ? html`<div class="promo-msg ok">
                      Code applied —
                      −${formatMajorAmount(model.discount, model.currency)}
                    </div>`
                  : nothing}
            </div>

            ${renderCheckoutTotals({
              subtotal: model.subtotal,
              discount: model.discount,
              quote: model.quote,
              currency: model.currency,
            })}

            <div class="ssl">${ICON_LOCK} Secure SSL Checkout</div>`}
  `;
}

export interface BookingConfirmationViewModel {
  kind: "class" | "appointment";
  accent: string;
  orgId: number;
  email: string;
  firstName: string;
  lastName: string;
  title: string;
  subline: string;
  price: string;
  orderId: string;
  startIso: string;
  endIso: string;
  hideSetupAccount: boolean;
  onBookAnother: () => void;
  onShare: () => void;
  onAccountSetup: (firstName?: string) => void;
  onSignInSetup: () => void;
}

export function renderBookingConfirmation(
  model: BookingConfirmationViewModel,
): TemplateResult {
  return html`<zipper-confirmation
    kind=${model.kind}
    .accent=${model.accent}
    .orgId=${model.orgId}
    .email=${model.email}
    .firstName=${model.firstName}
    .lastName=${model.lastName}
    .cTitle=${model.title}
    .subline=${model.subline}
    .price=${model.price}
    .orderId=${model.orderId}
    .startIso=${model.startIso}
    .endIso=${model.endIso}
    .hideSetupAccount=${model.hideSetupAccount}
    @zipper-confirm-book-another=${model.onBookAnother}
    @zipper-confirm-share=${model.onShare}
    @zipper-confirm-account-setup=${(
      event: CustomEvent<{ firstName?: string }>,
    ) => model.onAccountSetup(event.detail?.firstName)}
    @zipper-confirm-setup=${model.onSignInSetup}
  ></zipper-confirmation>`;
}

export interface IntakeQuestionViewModel {
  value: string;
  error: string | null;
  showError: boolean;
  onWrite: (value: string) => void;
  onTouch: () => void;
  parseMultiSelect: (value: string) => string[];
}

export function renderIntakeQuestion(
  question: FormQuestion,
  model: IntakeQuestionViewModel,
): TemplateResult {
  const set = (event: Event) =>
    model.onWrite((event.target as HTMLInputElement).value);
  const className = `fld${model.showError ? " invalid" : ""}`;
  const label = html`${question.label}${question.required
    ? html`<span class="fld-req" aria-hidden="true">*</span>`
    : nothing}`;
  const errorElement = model.showError
    ? html`<div class="fld-err" data-err-for=${question.key} role="alert">
        ${model.error}
      </div>`
    : nothing;

  if (question.type === "captcha") {
    return html``;
  }
  if (question.type === "signature") {
    return html`<label class=${className} data-qtype="signature"
      >${label}
      <input
        class="sig-input"
        type="text"
        placeholder="Type your full legal name"
        .value=${model.value}
        @input=${set}
        @blur=${model.onTouch}
      />
      <span class="sig-hint"
        >Type your full legal name as your electronic signature</span
      >
      ${errorElement}
    </label>`;
  }
  if (question.type === "textarea") {
    return html`<label class=${className} data-qtype="textarea"
      >${label}<textarea
        .value=${model.value}
        @input=${set}
        @blur=${model.onTouch}
      ></textarea>
      ${errorElement}
    </label>`;
  }
  if (question.type === "single-select" || question.type === "multi-select") {
    const multiple = question.type === "multi-select";
    const selected = multiple
      ? model.parseMultiSelect(model.value)
      : model.value
        ? [model.value]
        : [];
    const toggle = (option: string) => {
      model.onTouch();
      if (!multiple) {
        model.onWrite(selected[0] === option ? "" : option);
        return;
      }
      const next = selected.includes(option)
        ? selected.filter((candidate) => candidate !== option)
        : [...selected, option];
      model.onWrite(next.length ? JSON.stringify(next) : "");
    };
    return html`<div class=${className} data-qtype=${question.type}>
      <span>${label}</span>
      <div
        class="opt-chips${model.showError ? " invalid" : ""}"
        role=${multiple ? "group" : "radiogroup"}
      >
        ${question.options.map(
          (option) =>
            html`<button
              type="button"
              class="opt-chip"
              aria-pressed=${selected.includes(option)}
              @click=${() => toggle(option)}
            >
              ${option}
            </button>`,
        )}
      </div>
      ${errorElement}
    </div>`;
  }
  if (question.type === "dropdown") {
    return html`<label class=${className} data-qtype="dropdown"
      >${label}
      <select class="fld-select" @change=${set} @blur=${model.onTouch}>
        <option value="">Select an option</option>
        ${question.options.map(
          (option) =>
            html`<option value=${option} ?selected=${option === model.value}>
              ${option}
            </option>`,
        )}
      </select>
      ${errorElement}
    </label>`;
  }
  const inputType =
    question.type === "email"
      ? "email"
      : question.type === "phone-number"
        ? "tel"
        : question.type === "date"
          ? "date"
          : "text";
  return html`<label class=${className} data-qtype=${question.type}
    >${label}<input
      type=${inputType}
      .value=${model.value}
      @input=${set}
      @blur=${model.onTouch}
    />
    ${errorElement}
  </label>`;
}

const ICON_PEOPLE = svg`<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;

export interface OtherAttendeesViewModel {
  isGuest: boolean;
  forSomeoneElse: boolean;
  hasRecipientType: boolean;
  includeSelf: boolean;
  isAppointment: boolean;
  totalCapacity: number;
  count: number;
  recipientModal: TemplateResult | typeof nothing;
  attendeeForms: readonly TemplateResult[];
  onToggleSomeoneElse: () => void;
  onChangeRecipientType: () => void;
  onToggleIncludeSelf: () => void;
  onSetCount: (count: number) => void;
}

export function renderOtherAttendees(
  model: OtherAttendeesViewModel,
): TemplateResult {
  const totalSpots = (model.includeSelf ? 1 : 0) + model.count;
  const nounPlural = model.isGuest ? "guests" : "family members";
  const description =
    model.forSomeoneElse && model.hasRecipientType
      ? `${totalSpots} spot${totalSpots === 1 ? "" : "s"} reserved${
          model.includeSelf ? ` · you + ${model.count}` : ` · ${model.count}`
        } ${nounPlural}`
      : "Booking spots for family or guests too? Tap to reserve extra spots and add their details.";

  return html`
    <div class="others ${model.forSomeoneElse ? "on" : ""}">
      <button class="others-toggle" @click=${model.onToggleSomeoneElse}>
        <span class="ic">${ICON_PEOPLE}</span>
        <span>
          <span class="t">I'm booking this for someone else</span>
          <span class="s">${description}</span>
        </span>
        <span class="switch ${model.forSomeoneElse ? "on" : ""}"></span>
      </button>
      ${model.recipientModal}
      ${model.forSomeoneElse && model.hasRecipientType
        ? html`
            <div class="scenario-summary">
              <span
                >Booking for
                <b>${model.isGuest ? "guest(s)" : "family member(s)"}</b></span
              >
              <button class="add-link" @click=${model.onChangeRecipientType}>
                Change
              </button>
            </div>
            ${model.isAppointment && model.totalCapacity === 1
              ? nothing
              : html`<div class="self-row">
                  <div>
                    <div class="t">
                      ${model.isAppointment
                        ? "I'm attending this appointment too"
                        : "I'm attending this class too"}
                    </div>
                    <div class="s">
                      ${model.includeSelf
                        ? `Reserves a spot for you on top of the ${nounPlural} below.`
                        : `Only the ${nounPlural} below get a spot — you're just booking.`}
                    </div>
                  </div>
                  <span
                    class="switch ${model.includeSelf ? "on" : ""}"
                    style="margin-left:auto"
                    @click=${model.onToggleIncludeSelf}
                  ></span>
                </div>`}
            <div class="count-row">
              <div>
                <div class="t">
                  How many ${model.isGuest ? "guests" : "family members"}?
                </div>
                ${model.isAppointment && model.totalCapacity === 1
                  ? nothing
                  : html`<div class="s">
                      Spots in this class besides your own — ${totalSpots}
                      total.
                    </div>`}
              </div>
              <div class="stepper">
                <button @click=${() => model.onSetCount(model.count - 1)}>
                  −
                </button>
                <span class="n">${model.count}</span>
                <button @click=${() => model.onSetCount(model.count + 1)}>
                  +
                </button>
              </div>
            </div>
            ${model.attendeeForms}
            <button
              class="add-link"
              @click=${() => model.onSetCount(model.count + 1)}
            >
              + Add another ${model.isGuest ? "guest" : "family member"}
            </button>
          `
        : nothing}
    </div>
  `;
}

const ICON_CHECK = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;
const ICON_CHEVRON = svg`<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;

export interface PaymentChoiceAttendee {
  attendee: Attendee;
  label: string;
  isBuyer: boolean;
  radioIndex: number;
}

export interface PaymentChoicesViewModel {
  attendees: readonly PaymentChoiceAttendee[];
  tiers: readonly PricingTier[];
  /** Org ISO-4217 currency; USD when unset. */
  currency?: string | undefined;
  /** PLAT-6791: the tiers one attendee may choose from (see `PricingTier.audience`). */
  tiersFor: (attendee: PaymentChoiceAttendee) => readonly PricingTier[];
  unavailableFor?: (attendee: PaymentChoiceAttendee) => boolean;
  signedIn: boolean;
  familyPackAvailable: boolean;
  recipientType: "" | "family" | "guest";
  buyOptionsOpen: boolean;
  nextAction: TemplateResult;
  memberCreditsFor: (
    relationship: "self" | "family" | "guest",
  ) => readonly PaymentCreditView[];
  onSetTier: (attendee: PaymentChoiceAttendee, tierId: string) => void;
  onSetMemberCredit: (
    attendee: PaymentChoiceAttendee,
    productId: string,
    punchCardId?: string,
  ) => void;
  onSetFamilyCredit: (
    attendee: PaymentChoiceAttendee,
    useCredit: boolean,
  ) => void;
  onToggleBuyOptions: () => void;
}

interface PaymentCreditView {
  productId: string;
  guestsOnly?: boolean;
  punchCardId?: string;
  label: string;
  remaining: number | null;
}

export function renderPaymentChoices(
  model: PaymentChoicesViewModel,
): TemplateResult {
  const multiple = model.attendees.length > 1;
  return html`
    ${multiple
      ? html`<div class="pay-group-sub">
          Choose a payment option for each person.
        </div>`
      : nothing}
    ${model.attendees.map((entry) => {
      if (model.unavailableFor?.(entry)) {
        const message = html`<div class="pay-group-sub" role="status">
          Payment options are unavailable. Please refresh and try again.
        </div>`;
        return multiple
          ? html`<div class="payment-person">
              <div class="pay-group-title">${entry.label}</div>${message}
            </div>`
          : message;
      }
      const attendee = entry.attendee;
      const tiers = model.tiersFor(entry);
      const canShare =
        model.familyPackAvailable &&
        !isPack(findTier([...model.tiers], attendee.tierId));
      const relationship: "self" | "family" | "guest" = entry.isBuyer
        ? "self"
        : model.recipientType === "guest"
          ? "guest"
          : "family";
      const memberCredits = model.memberCreditsFor(relationship);
      const usingMemberCredit = !!attendee.memberCreditProductId;
      const hasCreditGroup = memberCredits.length > 0 || canShare;
      const coveredNoCharge =
        usingMemberCredit || (attendee.useCredit && canShare);
      const showBuyAccordion =
        model.signedIn &&
        hasCreditGroup &&
        coveredNoCharge &&
        tiers.length > 0;

      return html`
        <div class="payment-person">
          ${multiple
            ? html`<div class="pay-group-title">${entry.label}</div>`
            : nothing}
          ${hasCreditGroup
            ? html`
                ${memberCredits.map(
                  (credit) =>
                    html`<label
                      class="credit-line ${attendee.memberCreditProductId === credit.productId && attendee.memberCreditPunchCardId === credit.punchCardId ? "selected" : ""}"
                    >
                      <input
                        type="radio"
                        name="pay-${entry.radioIndex}"
                        .checked=${attendee.memberCreditProductId ===
                        credit.productId && attendee.memberCreditPunchCardId ===
                        credit.punchCardId}
                        @change=${() =>
                          model.onSetMemberCredit(entry, credit.productId, credit.punchCardId)}
                      />
                      <span class="credit-copy">
                        <span class="credit-title">${credit.label}</span>
                        <span class="credit-meta">
                          ${credit.guestsOnly ? "Use your guest pass" : "Use your credit"}
                          · ${credit.remaining === null ? "Unlimited access" : `${credit.remaining} left`}
                        </span>
                      </span>
                    </label>`,
                )}
                ${canShare
                  ? html`<label class="credit-line">
                      <input
                        type="checkbox"
                        .checked=${attendee.useCredit}
                        @change=${(event: Event) =>
                          model.onSetFamilyCredit(
                            entry,
                            (event.target as HTMLInputElement).checked,
                          )}
                      />
                      Use a credit from the family pack
                    </label>`
                  : nothing}
                ${coveredNoCharge
                  ? html`<div class="credit-covered">
                      <span class="chk">${ICON_CHECK}</span>
                      Covered by your credits — no charge
                    </div>`
                  : nothing}
              `
            : nothing}
          ${showBuyAccordion
            ? html`<button
                type="button"
                class="buy-acc-head"
                aria-expanded=${model.buyOptionsOpen}
                @click=${model.onToggleBuyOptions}
              >
                <span class="chev ${model.buyOptionsOpen ? "open" : ""}"
                  >${ICON_CHEVRON}</span
                >
                Other ways to pay
              </button>`
            : hasCreditGroup && tiers.length
              ? html`<div class="pay-divider"></div>`
              : nothing}
          ${tiers.length && (!showBuyAccordion || model.buyOptionsOpen)
            ? html`
                ${!showBuyAccordion && !multiple
                  ? html`<div class="pay-group-sub">
                      Buy a drop-in, class pack, or membership.
                    </div>`
                  : nothing}
                ${tiers.map(
                  (tier) =>
                    html`<button
                      type="button"
                      class="tier"
                      aria-pressed=${!usingMemberCredit &&
                      !(attendee.useCredit && canShare) &&
                      attendee.tierId === tier.id}
                      @click=${() => model.onSetTier(entry, tier.id)}
                    >
                      <div>
                        <div>
                          ${tier.label}
                          ${tier.badge
                            ? html`<span class="badge">${tier.badge}</span>`
                            : ""}
                        </div>
                        <div class="sub">${tier.sub}</div>
                      </div>
                      <span class="price"
                        >${formatShortAmount(tier.price, model.currency)}</span
                      >
                    </button>`,
                )}
              `
            : nothing}
        </div>
      `;
    })}
    <div class="payment-actions">${model.nextAction}</div>
  `;
}
